import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Grid,
  Avatar,
  Card,
  CardContent,
  InputAdornment,
  CircularProgress,
  Alert,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { getTransferRecords, getAssociates } from '../../services/associate';
import { hasWritePermission } from '../../utils/menuFilter';
import RefreshButton from '../../components/RefreshButton/RefreshButton';

const AssociateChange = () => {
  const canWrite = hasWritePermission('associate.associate-change');
  const history = useHistory();
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedAssociate, setSelectedAssociate] = useState(null);

  // API state management
  const [transferData, setTransferData] = useState([]);
  const [associates, setAssociates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const fetchTransferRecords = async (search = '', pageNum = 1, limit = 10) => {
    setLoading(true);
    setError(null);
    try {
      // Build params object for the service call
      const params = {
        page: pageNum,
        limit: limit,
      };

      // Only add search parameter if it's not empty and has at least 1 character
      if (search && search.trim().length > 0) {
        params.search = search.trim();
      }

      const result = await getTransferRecords(params);

      if (result.status) {
        setTransferData(result.data || []);
        setTotalRecords(result.extra?.total || 0);
      } else {
        throw new Error(result.message || 'API returned status: false');
      }
    } catch (err) {
      // Handle detailed backend validation errors
      let errorMessage = 'Failed to fetch transfer records';
      if (err.errors && Array.isArray(err.errors)) {
        // Show the first validation error message
        errorMessage = err.errors[0]?.msg || err.message || errorMessage;
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssociates = async () => {
    try {
      const result = await getAssociates();
      if (result.status) {
        setAssociates(result.data || []);
      }
    } catch (err) {
      console.error('Error fetching associates:', err);
    }
  };

  // Effects
  useEffect(() => {
    fetchTransferRecords();
    fetchAssociates();
  }, []);

  useEffect(() => {
    fetchTransferRecords(appliedSearchTerm, page + 1, rowsPerPage);
  }, [appliedSearchTerm, page, rowsPerPage]);

  const handleViewDetails = (associate) => {
    setSelectedAssociate(associate);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedAssociate(null);
  };

  const handleSearch = () => {
    setAppliedSearchTerm(searchTerm);
    setPage(0); // Reset to first page when searching
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setAppliedSearchTerm('');
    setPage(0);
  };

  const handleOpenTransferPage = () => {
    history.push('/app/user/AssociateTransfer');
  };

  const handleRefresh = () => {
    fetchTransferRecords(appliedSearchTerm, page + 1, rowsPerPage);
    fetchAssociates();
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f9f9fb', minHeight: '100vh' }}>
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Associate Transfer
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage and track associate transfers between sponsors
            </Typography>
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            {canWrite && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleOpenTransferPage}
                sx={{
                  fontWeight: 600,
                  px: 2,
                  py: 0.8,
                  textTransform: 'none',
                  fontSize: '13px',
                  boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                  borderRadius: 2,
                }}
              >
                Transfer Sponsor
              </Button>
            )}
            <RefreshButton onClick={handleRefresh} loading={loading} />
          </Box>
        </Box>
      </Box>

      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Box display="flex" gap={1} alignItems="center">
            <TextField
              fullWidth
              placeholder="Search by associate name, from sponsor, or to sponsor"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              startIcon={<SearchIcon />}
              sx={{
                px: 3,
                py: 1.875,
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(83, 109, 254, 0.3)',
                minWidth: '120px'
              }}
            >
              Search
            </Button>
            {appliedSearchTerm && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleClearSearch}
                sx={{ px: 2, textTransform: 'none', borderRadius: 2 }}
              >
                Clear
              </Button>
            )}
          </Box>
          {appliedSearchTerm && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Showing results for: <strong>"{appliedSearchTerm}"</strong>
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, bgcolor: '#ffebee' }}>
          <CardContent>
            <Alert severity="error" sx={{ mb: 0 }}>
              <strong>API Error:</strong> {error}
            </Alert>
          </CardContent>
        </Card>
      )}

      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: 14 }}>S.No</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 14 }}>Transfer Date</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 14 }}>Associate Name</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 14 }}>Transfer From Sponsor</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 14 }}>Transfer To Sponsor</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 14, textAlign: 'center' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
                      <CircularProgress size={24} />
                      <Typography color="text.secondary">Loading transfer records...</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : transferData.length > 0 ? (
                transferData.map((item, index) => (
                  <TableRow key={item._id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{new Date(item.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#536DFE' }}>{item.associateName}</TableCell>
                    <TableCell>{item.transferFromSponsorName}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{item.transferToSponsorName}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleViewDetails(item)}
                        sx={{ border: '1px solid #e0e0e0' }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {appliedSearchTerm
                        ? `No transfer records found for "${appliedSearchTerm}"`
                        : 'No transfer records found'
                      }
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalRecords}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          
          sx={{
            borderTop: '1px solid #e0e0e0',
            '& .MuiTablePagination-toolbar': {
              py: 2,
            },
          }}
        />
      </Card>

      {/* View Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}
      >
        <DialogTitle
          sx={{
            bgcolor: '#536DFE',
            color: '#fff',
            fontWeight: 700,
            fontSize: 18,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 2,
          }}
        >
          <PersonIcon sx={{ fontSize: 24 }} />
          Associate Details
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedAssociate && (
            <Grid container spacing={3}>
              <Grid item xs={12} display="flex" justifyContent="center">
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: '#536DFE',
                    fontSize: '2rem',
                    fontWeight: 700,
                  }}
                >
                  {selectedAssociate.associateName.charAt(0).toUpperCase()}
                </Avatar>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">ID</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '16px' }}>
                    {(() => {
                      // Find the associate's associateId by matching associateId (_id) with associates array
                      const associate = associates.find(a =>
                        a._id === selectedAssociate.associateId ||
                        a.id === selectedAssociate.associateId
                      );
                      return associate ? associate.associateId : selectedAssociate.associateId;
                    })()}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Name</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '16px' }}>
                    {selectedAssociate.associateName}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Transfer Details</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>From Sponsor:</strong> {selectedAssociate.transferFromSponsorName}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>From Sponsor ID:</strong> {(() => {
                        const sponsor = associates.find(a =>
                          a._id === selectedAssociate.transferFromSponsorId ||
                          a.id === selectedAssociate.transferFromSponsorId
                        );
                        return sponsor ? sponsor.associateId : selectedAssociate.transferFromSponsorId;
                      })()}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>To Sponsor:</strong> {selectedAssociate.transferToSponsorName}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>To Sponsor ID:</strong> {(() => {
                        const sponsor = associates.find(a =>
                          a._id === selectedAssociate.transferToSponsorId ||
                          a.id === selectedAssociate.transferToSponsorId
                        );
                        return sponsor ? sponsor.associateId : selectedAssociate.transferToSponsorId;
                      })()}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Timestamps</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Created:</strong> {new Date(selectedAssociate.createdAt).toLocaleString('en-IN')}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Updated:</strong> {new Date(selectedAssociate.updatedAt).toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
          <Button
            onClick={handleCloseViewDialog}
            variant="contained"
            sx={{
              textTransform: 'none',
              px: 3,
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssociateChange;
